import "./ChatBanner.less";
import { useAppDispatch, useAppSelector } from "../hooks";
import { useCallback, useEffect } from "react";
import { clearChatBanner, showChatBanner } from "../customerAppSlice";
import { ChatState } from "@Models/chat/ChatState";
import { PersistedMessageType } from "@Src/hubs/chat/dtos/PersistedMessageType";
import { ClientRole } from "@Src/hubs/chat/dtos/ClientRole";
import { ClientMessageType } from "@Src/hubs/chat/dtos/ClientMessageType";
import {
    OfflineRepBannerMessage,
    PlatinumChatWithRepBannerMessage,
    PlatinumChatBotBannerMessage,
} from "./BannerMessages";

export enum CustomerChatMessageEnum {
    NONE,
    PLATINUM_BOT_CHAT_BANNER_MESSAGE,
    PLATINUM_CHAT_WITH_REP_BANNER_MESSAGE,
    OFFLINE_REP_BANNER_MESSAGE,
}

const CustomerChatBanner = (): JSX.Element => {
    const { chatBannerState, operatorsOnline, chat, hasChatEnded, messages } =
        useAppSelector((state) => state.customerApp);

    const dispatch = useAppDispatch();

    const messageComponents: Record<CustomerChatMessageEnum, JSX.Element> = {
        [CustomerChatMessageEnum.PLATINUM_BOT_CHAT_BANNER_MESSAGE]: (
            <PlatinumChatBotBannerMessage />
        ),
        [CustomerChatMessageEnum.PLATINUM_CHAT_WITH_REP_BANNER_MESSAGE]: (
            <PlatinumChatWithRepBannerMessage />
        ),
        [CustomerChatMessageEnum.OFFLINE_REP_BANNER_MESSAGE]: (
            <OfflineRepBannerMessage />
        ),
        [CustomerChatMessageEnum.NONE]: <></>,
    };

    function getMessage(): JSX.Element {
        return messageComponents[chatBannerState.chatBannerMessage] || <></>;
    }

    const handleOperatorsOfflineBanner = useCallback((): void => {
        dispatch(
            showChatBanner({
                chatBannerMessage:
                    CustomerChatMessageEnum.OFFLINE_REP_BANNER_MESSAGE,
                // Make sure operators are online to allow for this banner to show
                active: !operatorsOnline && !chatBannerState.isDismissed,
            })
        );
    }, [chatBannerState.isDismissed, dispatch, operatorsOnline]);

    const handlePlatinumBanner = useCallback((): void => {
        const customerHasSentMessage = messages.some(
            (x) =>
                x.clientRole === ClientRole.Customer &&
                ![
                    PersistedMessageType.UserJoinedChat,
                    PersistedMessageType.UserLeftChat,
                    ClientMessageType.ChatCreated,
                ].includes(x.messageType)
        );

        if (customerHasSentMessage) {
            dispatch(
                showChatBanner({
                    chatBannerMessage:
                        CustomerChatMessageEnum.PLATINUM_CHAT_WITH_REP_BANNER_MESSAGE,
                    active: operatorsOnline,
                    isDismissed: false,
                })
            );
        } else {
            dispatch(
                showChatBanner({
                    chatBannerMessage:
                        CustomerChatMessageEnum.PLATINUM_BOT_CHAT_BANNER_MESSAGE,
                    active: !chatBannerState.isDismissed,
                })
            );
        }
    }, [chatBannerState, dispatch, messages, operatorsOnline]);

    useEffect(() => {
        // If the customer is not talking to the bot or the chat has ended, clear the banner
        if (chat?.state !== ChatState.TalkingToBot || hasChatEnded) {
            dispatch(clearChatBanner());
            return;
        }

        if (chat?.isPlatinum) {
            handlePlatinumBanner();
        } else {
            handleOperatorsOfflineBanner();
        }
    }, [
        chat?.isPlatinum,
        chat?.state,
        chatBannerState.isDismissed,
        dispatch,
        handleOperatorsOfflineBanner,
        handlePlatinumBanner,
        hasChatEnded,
        operatorsOnline,
    ]);

    return chatBannerState.active ? (
        <div className="chat-banner" data-testid="customer-chat-banner">
            {getMessage()}
        </div>
    ) : (
        <></>
    );
};

export default CustomerChatBanner;
